import slugify from 'slugify'
import { formatMoney } from '../helpers'
import { quantityIncrementor } from './misc'
import {
  templateCustomerDiscountItem,
  templateDiscountItem,
} from './cart-discounts'

const minicartItemImage = async item => `
  <a
    href="${item.url}"
    title="${item.product_title}"
    class="mini-cart-item__image"
  >
    <figure class="mini-cart-item__image-wrap">
      ${
        item.image
          ? `<img class="mini-cart-item__image-img" src="${item.image}" sizes="7em" alt="${item.product_title}">`
          : ''
      }
    </figure>
  </a>
`

const minicartItemTitle = async item => `
  <a
    href="${item.url}"
    class="mini-cart-item__info-title"
  >
    <h4>
      ${item.product_title}
    </h4>

    ${item?.metafields?.product?.short_description ? `<p>${item.metafields.product.short_description}</p>` : ''}
  </a>
`

const minicartItemVariant = async item => {
  if (
    item.options_with_values.length === 0 ||
    (item.options_with_values.length === 1 &&
      item.options_with_values[0].name === 'Title')
  ) {
    return ''
  }

  return item.options_with_values
    .map(({ name, value }) => {
      const optionClassName = slugify(name)
      const valueClassName = slugify(value)

      return `
      <div class="mini-cart-item__info-variant">
        <span class="mini-cart-item__info-variant-name mini-cart-item__info-variant-name--${optionClassName}">
          ${name}:
        </span>

        <span class="mini-cart-item__info-variant-value mini-cart-item__info-variant-value--${valueClassName}">
          ${value}
        </span>
      </div>
    `
    })
    .join('\n')
}

const minicartItemProperties = async item => {
  const properties = Object.entries(item.properties)
  if (properties.length === 0) {
    return ''
  }

  return properties
    .map(([name, value]) => {
      const optionClassName = slugify(name)
      const valueClassName = slugify(value)

      if (!value || value === '') {
        return ''
      }

      return `
        <div class="mini-cart-item__info-property">
          <span class="mini-cart-item__info-property-name mini-cart-item__info-property-name--${optionClassName}">
            ${name}:
          </span>

          <span class="mini-cart-item__info-property-value mini-cart-item__info-property-value--${valueClassName}">
            ${value}
          </span>
        </div>
      `
    })
    .join('\n')
}

const minicartItemPrice = async item => `
  <div class="mini-cart-item__info-price">
    ${
      item.final_line_price !== item.original_line_price
        ? `
      <s class="mini-cart-item__info-price--original">${formatMoney(item.original_line_price)}</s>
    `
        : ''
    }
    ${formatMoney(item.final_line_price)}
  </div>
`

const minicartItemDiscounts = async item => `
  <ul class="mini-cart-item__info-discounts">
    ${(
      await Promise.all(
        item.line_level_discount_allocations.map(
          ({ amount, discount_application }) => {
            const data = {
              key: discount_application.key,
              title: discount_application.title,
              amount,
            }

            return discount_application.type === 'discount_code'
              ? templateCustomerDiscountItem(data)
              : templateDiscountItem(data)
          },
        ),
      )
    ).join('')}
  </ul>
`

const minicartItemSellingPlans = async (item, lineId) => {
  if (!item.sellingPlanGroups) {
    return ''
  }

  const currentPlanId =
    item.selling_plan_allocation?.selling_plan?.id.toString()

  return item.sellingPlanGroups
    .map(
      group => `
    <div class="mini-cart-item__info-plan-group">
      <label class="mini-cart-item__info-plan-group-label">
        <input data-selling-plan-toggle type="checkbox" class="mini-cart-item__info-plan-group-toggle" data-line-id="${lineId}" ${group.sellingPlans.find(plan => plan.id === currentPlanId) !== undefined ? 'checked' : ''} />
        <h5 class="mini-cart-item__info-plan-group-title">${group.name}</h5>
      </label>

      ${
        group.sellingPlans.length > 1
          ? `
              <select data-selling-plan-select data-line-id="${lineId}" class="mini-cart-item__info-plan-select">
                ${group.sellingPlans
                  .map(
                    plan => `
                  <option value="${plan.id}" ${plan.id === currentPlanId ? 'selected' : ''}>${plan.name}</option>
                `,
                  )
                  .join('')}
              </select>
            `
          : `
              <input type="hidden" data-selling-plan-select data-line-id="${lineId}" value="${group.sellingPlans[0].id}" />
              <span class="mini-cart-item__info-plan-name">${group.sellingPlans[0].name}</span>
            `
      }
    </div>
  `,
    )
    .join('')
}

const minicartItemRemoveButton = async (item, lineId) => `
  <a
    href="/cart/change?line=${lineId}&amp;quantity=0"
    class="mini-cart-item__info-clear"
    data-remove-line-item
    data-line-id="${lineId}"
  >
    <span class="screenreader-text">${window.theme.strings.cart.label.remove}</span>
    <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 5.17285H14.9442" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
      <path d="M5.23438 4.67479V3.00358C5.23423 2.80608 5.26697 2.61049 5.33072 2.42799C5.39447 2.24549 5.48797 2.07967 5.60589 1.94002C5.7238 1.80036 5.8638 1.68962 6.01788 1.61411C6.17197 1.53861 6.33711 1.49983 6.50386 1.5H9.2562C9.42295 1.49983 9.5881 1.53861 9.74218 1.61411C9.89627 1.68962 10.0363 1.80036 10.1542 1.94002C10.2721 2.07967 10.3656 2.24549 10.4293 2.42799C10.4931 2.61049 10.5258 2.80608 10.5257 3.00358V4.67479" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10"/>
      <path d="M6.29297 8.90771L6.29297 12.0825" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
      <path d="M9.46875 8.90771L9.46875 12.0825" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
      <path d="M2.05859 4.6748L2.95383 14.6783C3.04613 15.7096 3.91038 16.5 4.94586 16.5H11.1246C12.184 16.5 13.0597 15.6738 13.1212 14.6162L13.6995 4.6748" stroke="var(--foreground)" stroke-width="2"/>
    </svg>
  </a>
`

export const templateMinicartItem = async (item, lineId) => {
  console.log(item)

  return `
    <div class="mini-cart-items__item mini-cart-item" data-line-id="${lineId}" aria-live="polite">
      ${await minicartItemImage(item)}

      <div class="mini-cart-item__info-content">
        ${await minicartItemTitle(item)}
        ${await minicartItemVariant(item)}
        ${await minicartItemProperties(item)}
        ${await minicartItemSellingPlans(item, lineId)}
      </div>

      ${await minicartItemRemoveButton(item, lineId)}
      ${await quantityIncrementor(item.quantity, item.variant_id, lineId, 0, item.variant_metafields?.product?.quantity_increment || 1)}
      ${await minicartItemPrice(item)}

      ${await minicartItemDiscounts(item)}
    </div>
  `
}
